// Global Instructions Rule Applied!
//
// The "Part of consolidated distribution" banner on the standard Distribution
// form (docs/WATERFALL_CARRY_DESIGN.md, phase 5), on the Capital Call form's
// ConsolidatedCallBanner chrome. A consolidated fund's distribution is a
// header with ONE distributions row per legal entity; the LEAD row (the
// primary fund's) holds the inputs and runs the lifecycle, the satellite
// entity rows are derived. Three modes:
//   - 'new'    — the form was opened via "New Consolidated Distribution"
//                (?fund_group=): the first save creates the header and the
//                lead row on the primary fund.
//   - 'lead'   — the lead row of an existing consolidated distribution: the
//                entities with their part of the proceeds, their share, the
//                lines, their status and booking.
//   - 'entity' — a satellite entity row: read-only, the lifecycle runs on
//                the consolidated fund; a link opens the lead.
// Purely presentational: the parent form owns the actions.

import React from 'react'
import { Alert, Button, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import NumberText from '../../../../core/components/NumberText.tsx'
import type {
  DistributionConsolidated,
  DistributionConsolidatedEntity
} from '../../../../state_manager/data_models/distribution_detail.ts'

const { Text } = Typography

interface ConsolidatedDistributionBannerProps {
  mode: 'new' | 'lead' | 'entity'
  groupName?: string | null
  /** the detail's prog__consolidated block (null in 'new' mode) */
  consolidated?: DistributionConsolidated | null
  /** 'entity' mode: opens the lead distribution in its own tab */
  onOpenLead?: () => void
}

const percent = (v: number | null | undefined): string => (v == null ? '—' : `${(v * 100).toFixed(2)}%`)

const statusTag = (label: string | null | undefined, bookedLabel: string): React.ReactNode =>
  label ? <Tag color={label === bookedLabel ? 'green' : 'blue'}>{label}</Tag> : <Text type='secondary'>—</Text>

const ENTITY_COLUMNS: ColumnsType<DistributionConsolidatedEntity> = [
  {
    title: 'Fund',
    key: 'fund',
    render: (_: unknown, e) => (
      <span>
        {e.funds__name ?? '—'}
        {e.is_lead ? (
          <Tag color='purple' className='ml-2'>
            Lead
          </Tag>
        ) : null}
      </span>
    )
  },
  {
    title: 'Part of proceeds',
    key: 'entity_gross',
    width: 150,
    align: 'right',
    render: (_: unknown, e) => (e.entity_gross == null ? <Text type='secondary'>—</Text> : <NumberText value={e.entity_gross} />)
  },
  { title: 'Share', key: 'share', width: 90, align: 'right', render: (_: unknown, e) => percent(e.share) },
  {
    title: 'Lines total',
    key: 'lines',
    width: 150,
    align: 'right',
    render: (_: unknown, e) =>
      e.prog__effective_total == null ? <Text type='secondary'>—</Text> : <NumberText value={e.prog__effective_total} />
  },
  {
    title: 'Status',
    key: 'status',
    width: 150,
    render: (_: unknown, e) => (
      <span>
        {statusTag(e.status_label, 'Issued')}
        {e.status_label === 'Draft' && e.needs_compute ? <Tag color='gold'>Needs Compute</Tag> : null}
        {e.prog__sba_failed ? <Tag color='gold'>SBA gate failed</Tag> : null}
      </span>
    )
  },
  {
    title: 'Booking',
    key: 'booking',
    width: 100,
    render: (_: unknown, e) => statusTag(e.transaction_status_label, 'Booked')
  }
]

export default function ConsolidatedDistributionBanner({
  mode,
  groupName,
  consolidated,
  onOpenLead
}: ConsolidatedDistributionBannerProps) {
  const name = groupName ?? consolidated?.fund_groups__name ?? null
  const title =
    mode === 'new'
      ? name
        ? `New consolidated distribution of ${name}`
        : 'New consolidated distribution'
      : name
        ? `Part of consolidated distribution of ${name}`
        : 'Part of a consolidated distribution'

  return (
    <Alert
      type='info'
      showIcon
      data-testid='consolidated-distribution-banner'
      message={title}
      description={
        <div className='flex flex-col gap-2'>
          {mode === 'new' ? (
            <Text type='secondary'>
              Creating saves the consolidated distribution and its lead row on the primary fund; one row per
              legal entity follows on Compute. The sources may be realizations of any member fund; each
              entity takes its part of the proceeds and runs its own waterfall on it. Compute, Issue and
              Revert run on every entity at once from this form or from the consolidated fund&apos;s
              Distributions tab.
            </Text>
          ) : mode === 'lead' ? (
            <Text type='secondary'>
              This is the lead row (the primary fund&apos;s): its name, date, kind, gross amount and sources
              are the consolidated distribution&apos;s. Compute splits the proceeds per legal entity and runs
              each entity&apos;s waterfall; Issue books every entity&apos;s share; Revert and Delete run on all
              of them. The lines below are this entity&apos;s part, not the whole.
            </Text>
          ) : (
            <Text type='secondary'>
              This is a legal entity&apos;s row, derived from the lead: read-only here. The lifecycle -
              Compute, Issue, Revert, Delete - runs on the consolidated fund, from the lead distribution or
              the consolidated fund&apos;s Distributions tab.
            </Text>
          )}
          {mode === 'entity' && onOpenLead ? (
            <div>
              <Button size='small' onClick={onOpenLead} disabled={!consolidated?.lead_distribution_id}>
                Open the consolidated distribution
              </Button>
            </div>
          ) : null}
          {consolidated && consolidated.entities.length > 0 ? (
            <Table<DistributionConsolidatedEntity>
              size='small'
              pagination={false}
              rowKey='distributions__id'
              columns={ENTITY_COLUMNS}
              dataSource={consolidated.entities}
              data-testid='consolidated-distribution-entities'
            />
          ) : null}
        </div>
      }
    />
  )
}
